import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import { config, DisableDateTypeEnum, errored_config, getDateFieldsList } from "../definitions";
import {
    DEFAULT_DATE_FORMAT,
    formatDate,
    formatNumber,
    formatString,
} from "../../../utils/formattingUtils";
import moment from "moment";
import { useAppSelector } from "../../../app/store/hooks";
import {
    UpdateArtifactDetailsByIdRequest,
    useUpdateArtifactDetailsByIdMutation,
} from "../../../endpoints/updateArtifactDetailsById";
import escapeRegExp from "lodash/escapeRegExp";
import { stateOptions } from "../../../features/filter-sheets/definitions";
import { parseISO } from "date-fns/parseISO";
import { isBefore } from "date-fns/isBefore";
import { isAfter } from "date-fns/isAfter";
import { useEditingDocument } from "../hooks";
import Link from "@amzn/meridian/link";
import { MailInfoSectionFieldsRenderer } from "./MailInfoSectionFieldsRenderer";
import Alert from "@amzn/meridian/alert";
import Loader from "@amzn/meridian/loader";
import _, { omit } from "lodash";

export interface IMailInfoSectionProps {
    documentType: string;
    propertyType: string;
    artifactId: string;
    artifactType: string;
    data: UpdateArtifactDetailsByIdRequest;
    status: string;
    assignedTo: string;
}

export const MailInfoSection: React.FC<IMailInfoSectionProps> = (props: IMailInfoSectionProps) => {
    const { alias } = useAppSelector(state => state.auth.user);
    const { documentType, propertyType } = props;

    const [updateArtifact, { isLoading, isError }] = useUpdateArtifactDetailsByIdMutation();
    const [assignedTo, setAssignedTo] = useState(props.assignedTo);
    const [status, setStatus] = useState(props.status);
    const [sections, setSections] = useState(
        config[propertyType]?.[_.defaultTo(documentType, "")] || []
    );

    const [searchQuery, setSearchQuery] = useState();
    const searchRegExp = new RegExp(escapeRegExp(searchQuery), "i");
    const { isEditing, setIsEditing, isAssignedToChanged } = useEditingDocument();
    const [formError, setFormError] = useState("");
    const onEditButton = () => {
        setIsEditing(true);
        setFormError("");
    };

    const [formData, setFormData] = useState({
        ...props.data.updatedArtifactDetails,
        ...props.data.updatedArtifactDetails.address,
    });

    useEffect(() => {
        setFormData({ ...formData, status: status });
    }, [status]);

    useEffect(() => {
        let updatedFormData = {
            ...props.data.updatedArtifactDetails,
            ...props.data.updatedArtifactDetails.address,
        };

        setFormData(updatedFormData);

        let updatedSections = config[propertyType]?.[documentType] || [];

        if (updatedSections.length === 0) {
            updatedSections =
                errored_config["FAILED"][props.data.updatedArtifactDetails?.failureReason];

            updatedFormData = {
                ...updatedFormData,
                associatedPropertyType: "",
                documentType: "",
            };

            setFormData(updatedFormData);
        }
        setSections(updatedSections);
    }, [propertyType, documentType, props.data]);

    useEffect(() => {
        setAssignedTo(props.assignedTo);
        setStatus(props.status);
        setFormData({
            ...formData,

            assignedTo: props.assignedTo,
            status: props.status,
        });
    }, [props.assignedTo, props.status]);

    const onSaveButton = async () => {
        const isDuplicateOrUnsorted =
            formData.failureReason === "Duplicate" || formData.failureReason === "Unsorted";

        if (
            formData["associatedPropertyType"] === "REAL_PROPERTY" &&
            formData["documentType"] === "TAX_RETURN"
        ) {
            setFormError("Tax return can not be assigned to Real estate");

            return;
        }

        const updatedFormData = updateFormData(formData);
        const fieldsToOmit = ["treasureStatus", "artifactType", "artifactId", "failureReason"];
        if (isDuplicateOrUnsorted) {
            const updatedStatus = status === "FAILED" ? "UNREAD" : status;
            const updatedArtifactDetails = getUpdatedArtifactDetails(
                updatedStatus,
                omit(updatedFormData, fieldsToOmit)
            );
            await handleAndUpdateArtifactDetails(updatedArtifactDetails);

            return;
        }

        const updatedStatus =
            isAssignedToChanged === true && status !== "OVERDUE"
                ? "UNREAD"
                : updatedFormData.status;
        const updatedArtifactDetails = getUpdatedArtifactDetails(
            updatedStatus,
            omit(updatedFormData, fieldsToOmit)
        );
        await handleAndUpdateArtifactDetails(updatedArtifactDetails);
    };

    const getUpdatedArtifactDetails = (updatedStatus: string, updatedFormData: any) => ({
        artifactType: props.artifactType,
        artifactId: props.artifactId,
        businessUseCase: "SMART",
        clientName: alias,
        updatedBy: alias,
        updatedArtifactDetails: {
            ...updatedFormData,
            assignedTo: assignedTo,
            status: updatedStatus,
            association: undefined,
            artifactId: undefined,
            artifactType: undefined,
        },
    });

    const handleAndUpdateArtifactDetails = async (
        updatedArtifactDetails: UpdateArtifactDetailsByIdRequest
    ) => {
        await handleUpdateArtifactDetails(updatedArtifactDetails).then(response => {
            if (response.data && response.data.status === "SUCCEEDED") {
                setIsEditing(false);
                setFormData({
                    ...updatedArtifactDetails.updatedArtifactDetails,
                    ...updatedArtifactDetails.updatedArtifactDetails.address,
                });
            }
        });
    };

    const updateFormData = (formData: any) => {
        let updatedFormData = updateDueDate(formData);
        updatedFormData = updateDateFields(updatedFormData);
        updatedFormData = updateAddressFields(updatedFormData);

        return updatedFormData;
    };

    const updateDueDate = (formData: any) => {
        const updatedFormData = { ...formData };

        if (!updatedFormData["dueDate"]) {
            return updatedFormData;
        }

        const isDatePassed = isAfter(parseISO(updatedFormData["dueDate"]), new Date());

        if (isDatePassed && updatedFormData.status === "OVERDUE") {
            updatedFormData.status = "OPEN";
            setStatus("OPEN");
        } else {
            updatedFormData.status = status;
        }

        return updatedFormData;
    };

    const updateDateFields = (updatedData: any) => {
        const dateFieldsList = getDateFieldsList();
        const updatedFormData = {
            ...updatedData,
            updatedBy: alias,
        };

        dateFieldsList.forEach(dateField => {
            if (updatedFormData[dateField]) {
                updatedFormData[dateField] = moment(updatedFormData[dateField]).format(
                    DEFAULT_DATE_FORMAT
                );
            }
        });

        return updatedFormData;
    };

    const updateAddressFields = (updatedData: any) => {
        const addressFields = [
            "county",
            "jurisdiction",
            "country",
            "streetAddress",
            "zipCode",
            "city",
            "state",
        ];

        const updatedFormData = { ...updatedData };

        addressFields.forEach(field => {
            if (updatedFormData[field] !== undefined) {
                updatedFormData.address = {
                    ...updatedFormData.address,
                    [field]: updatedFormData[field],
                };
                delete updatedFormData[field];
            }
        });

        return updatedFormData;
    };

    const handleUpdateArtifactDetails = async (data: UpdateArtifactDetailsByIdRequest) => {
        try {
            return await updateArtifact(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleChange = (field: string, value: any) => {
        setFormError("");
        setFormData(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const getAddressString = () => {
        const { streetAddress, city, state, zipCode } = formData.address || {};
        const addressParts = [streetAddress, city, state, zipCode];

        return addressParts.filter(Boolean).join(", ");
    };

    const getOptionLabel = (section: { options: any[] }, value: any) => {
        const option = section.options.find(opt => opt.value === value);

        return option ? option.label : value ? value : "-";
    };

    const getDisplayString = (section: { prefix: any; suffix: any }, value: any) => {
        if (section.prefix && value) {
            return `${section.prefix} ${value}`;
        } else if (section.suffix && value) {
            return `${value} ${section.suffix}`;
        } else {
            return `${value || "-"}`;
        }
    };

    const renderFieldValue = (section: any, formData: any) => {
        const isParcelOrAccountNumber =
            (section.field === "parcelNumber" ||
                section.field === "accountNumber" ||
                section.field === "bblCode" ||
                section.field === "propertyId") &&
            section.type === "input" &&
            formData[section.field] &&
            formData[section.field].length !== 0;

        if (isParcelOrAccountNumber) {
            const association = formData.association?.documentAssociation;

            if (association) {
                const value = association[0].parcelArtifactId;

                if (formData.treasureStatus === "UNASSOCIATED") {
                    return formatString(formData[section.field]);
                }

                return (
                    <Link
                        href={`/parcel-details/${value}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {formData[section.field]}
                    </Link>
                );
            }

            return formData[section.field];
        }

        switch (section.type) {
            case "select":
                return getOptionLabel(section, formData[section.field]);
            case "address":
                return formatString(getAddressString());
            case "input":
                return formatString(formData[section.field]);
            case "input-number":
                return getDisplayString(section, formatNumber(formData[section.field]));
            case "date":
                return formatDate(formData[section.field]);
            default:
                return formatString(formData[section.field] || "-");
        }
    };

    const getDisabledDates = (section: any, value: any) => {
        if (section.disabledDates) {
            switch (section.disabledDates) {
                case DisableDateTypeEnum.DATES_BEFORE_NOW:
                    return isBefore(parseISO(value), new Date());
                default:
                    return false;
            }
        }

        return false;
    };

    return (
        <>
            {isEditing === false && (
                <Box spacingInset="200">
                    {sections.map(section => (
                        <React.Fragment key={section.field}>
                            <Box>
                                <Text type="b300">{section.label}</Text>
                                <Text type="h100">{renderFieldValue(section, formData)}</Text>
                            </Box>
                            <Box height="15px" />
                        </React.Fragment>
                    ))}
                    <Button type="secondary" onClick={onEditButton}>
                        Edit
                    </Button>
                </Box>
            )}
            {isEditing === true && (
                <Box spacingInset="200">
                    {isLoading && <Loader type="linear" />}
                    {sections.map(section => (
                        <React.Fragment key={section.field}>
                            <Box>
                                <MailInfoSectionFieldsRenderer
                                    section={section}
                                    formData={formData}
                                    handleChange={handleChange}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    getDisabledDates={getDisabledDates}
                                    stateOptions={stateOptions}
                                    searchRegExp={searchRegExp}
                                />
                            </Box>
                            <Box height="15px" />
                        </React.Fragment>
                    ))}

                    {formError.trim() && (
                        <Row alignmentHorizontal="start">
                            <Alert type="error" size="small">
                                {formError}
                            </Alert>
                        </Row>
                    )}

                    {isError && (
                        <Row alignmentHorizontal="start">
                            <Alert type="error" size="small">
                                Unexpected error occurred while saving changes. Please try again.
                            </Alert>
                        </Row>
                    )}

                    <Row alignmentHorizontal="end">
                        <Button
                            type="secondary"
                            onClick={onSaveButton}
                            disabled={
                                formData["failureReason"] !== "Duplicate" &&
                                formData["failureReason"] !== "Combined" &&
                                (formData["associatedPropertyType"] === undefined ||
                                    formData["associatedPropertyType"] === "" ||
                                    formData["documentType"] === undefined ||
                                    formData["documentType"] === "")
                            }
                        >
                            Save
                        </Button>
                    </Row>
                </Box>
            )}
        </>
    );
};
